import React, { useState, useEffect, useRef } from "react";
import {
  FiActivity,
  FiBookOpen,
  FiCheck,
  FiChevronDown,
  FiDownload,
  FiEdit2,
  FiFileText,
  FiMessageSquare,
  FiSearch,
  FiSend,
  FiSettings,
  FiSliders,
} from "react-icons/fi";
import { FaSpinner } from "react-icons/fa";
import { FiTrash } from "react-icons/fi";
import { ModalOutputAdjustments } from "./components/ModalOutputAdjustments/ModalOutputAdjustments";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { getChatResponseData } from "./services/chatResponse.service";
import { useParams } from "react-router-dom";
import { IOutputAdjustment } from "./interfaces/OutputAdjustment";
import { IMessage } from "./interfaces/Message";
import Markdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import styles from "../../../../../HomePage/components/ChatBlock/components/ChatAI/ChatAI.module.css";
import { IoCopy } from "react-icons/io5";
import { PromptBlock } from "./components/PromptBlock/PromptBlock";
import { exportChatConv } from "./services/exportChatConv.service";
import {
  createMemoJob,
  waitForMemoJob,
  downloadMemoJob,
} from "../../../../services/generateMemos.service";
import { getTodayDocxFilename } from "../../../../../HomePage/helpers/getTodayDocxFilename";
import { toast } from "react-toastify";
import { TbRotateClockwise } from "react-icons/tb";
import handleCopyToClipboard from "../../../../../../helpers/handleCopyToClipboard";
import { MarkdownEditor } from "../MarkdownEditor/MarkdownEditor";
import { ChatLimitBanner } from "../../../../../../components/ChatLimitBanner/ChatLimitBanner";
import {
  ChatLimitReachedError,
  useChatRequestsLeft,
} from "../../../../../../helpers/chatRequestsLeft";

interface IChatAIProps {
  isDataFound: boolean;
}

const ChatAI: React.FC<IChatAIProps> = ({ isDataFound }) => {
  const { t } = useTranslation();
  const isLimitReached = useChatRequestsLeft() === 0;
  const [valueInput, setValueInput] = useState<string>("");
  const [valueConversationInput, setValueConversationInput] =
    useState<string>("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [waitingMessage, setWaitingMessage] = useState("Loading...");
  const [waitingSteps, setWaitingSteps] = useState<string[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPanelCollapsed, setIsPanelCollapsed] = useState(true);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const [messages, setMessages] = useState<IMessage[]>([]);
  const [conversationHistory, setConversationHistory] = useState<IMessage[]>(
    [],
  );
  const { regulator, id } = useParams<{ regulator: string; id: string }>();
  const [outputAdj, setOutputAdj] = useState<IOutputAdjustment>({
    tone: null,
    format: null,
    length: null,
  });
  const isWaitingPhase = useRef(true);
  const chunkCache = useRef<string[]>([]);
  const isRendering = useRef(false);
  const streamingBuffer = useRef<string>("");
  const lastUserMsgRef = useRef<HTMLDivElement | null>(
    null,
  ) as React.MutableRefObject<HTMLDivElement | null>;
  const [scrollToLastUser, setScrollToLastUser] = useState(false);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [isGeneratingMemoIndex, setIsGeneratingMemoIndex] = useState<
    number | null
  >(null);

  const stripStepEmoji = (label: string) =>
    label.replace(/^[^A-Za-z0-9]+\s*/, "");

  const getStepIcon = (label: string) => {
    const normalized = stripStepEmoji(label).toLowerCase();
    const iconClass =
      "w-4 h-4 flex-shrink-0 self-start text-slate-500 dark:text-slate-400";

    if (normalized.startsWith("analyse")) {
      return <FiActivity className={iconClass} />;
    }
    if (
      normalized.startsWith("collect") ||
      normalized.includes("search query") ||
      normalized.startsWith("iteration")
    ) {
      return <FiSearch className={iconClass} />;
    }
    if (
      normalized.startsWith("retrieve") ||
      normalized.startsWith("extracting") ||
      normalized.startsWith("agent started")
    ) {
      return <FiFileText className={iconClass} />;
    }
    if (
      normalized.startsWith("generate") ||
      normalized.startsWith("writing")
    ) {
      return <FiSettings className={iconClass} />;
    }
    if (normalized.startsWith("answer")) {
      return <FiMessageSquare className={iconClass} />;
    }

    return <FiFileText className={iconClass} />;
  };

  const lastUserMsgIdx = messages.map((m) => m.role).lastIndexOf("user");
  const lastAssistantMsgIdx = messages
    .map((m) => m.role)
    .lastIndexOf("assistant");
  const landingInputRef = useRef<HTMLTextAreaElement>(null);
  const convInputRef = useRef<HTMLTextAreaElement>(null);
  const MAX_INPUT_HEIGHT = 288;

  const autoResize = (el: HTMLTextAreaElement | null) => {
    if (!el) return;
    el.style.height = "auto";
    const nextHeight = Math.min(el.scrollHeight, MAX_INPUT_HEIGHT);
    el.style.height = `${nextHeight}px`;
    el.style.overflowY = el.scrollHeight > MAX_INPUT_HEIGHT ? "auto" : "hidden";
  };

  const [copiedToBuffer, setCopiedToBuffer] = useState<{
    [key: number]: boolean;
  }>({});
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState("");
  const markdownRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const copyTimeouts = useRef<Record<number, number>>({});

  const {
    mutate: fetchChatResponse,
    isPending,
    isError,
  } = useMutation({
    mutationFn: async () =>
      await getChatResponseData(
        regulator,
        id,
        conversationHistory,
        outputAdj,
        "details",
        handleStreamResponse,
        onFullResponse,
      ),
    onSuccess: () => {
      setWaitingMessage("");
    },
    onError: (error) => {
      console.error("Error fetching chat response:", error);
      if (!(error instanceof ChatLimitReachedError)) {
        toast.error(t("errorChatAIResponse"));
      }
      setMessages((prevMessages) => {
        const updated = [...prevMessages];
        const lastMsg = updated[updated.length - 1];
        if (lastMsg && lastMsg.role === "assistant" && lastMsg.text === "") {
          updated.pop();
        }
        return updated;
      });
    },
  });

  const isBusy = isPending || isGeneratingMemoIndex !== null;

  const { mutate: exportChat } = useMutation({
    mutationFn: async () => await exportChatConv(messages),
    onSuccess: (data: ArrayBuffer) => {
      const blob = new Blob([data], {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = getTodayDocxFilename();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    onError: (error) => {
      console.error("Error exporting chat conversation:", error);
    },
  });

  const onFullResponse = (fullResponse: string) => {
    const lastClearIndex = fullResponse.lastIndexOf("--CLEAR--");

    let responsePart = fullResponse.replace(/--KEEP-ALIVE--/g, "");
    if (lastClearIndex !== -1) {
      responsePart = responsePart.substring(
        lastClearIndex + "--CLEAR--".length,
      );
    }

    setMessages((prevMessages) => {
      const updatedMessages = [...prevMessages];

      const lastAssistantIndex = [...updatedMessages]
        .reverse()
        .findIndex((m) => m.role === "assistant");

      if (lastAssistantIndex !== -1) {
        const realIndex = updatedMessages.length - 1 - lastAssistantIndex;
        updatedMessages[realIndex] = {
          role: "assistant",
          text: responsePart,
        };
        return updatedMessages;
      }

      return [
        ...updatedMessages,
        {
          role: "assistant",
          text: responsePart,
        },
      ];
    });
  };

  const scrollChatToBottom = () => {
    const el = chatContainerRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  };

  const handleChatScroll = () => {
    const el = chatContainerRef.current;
    if (!el) return;
    const threshold = 24;
    const atBottom =
      el.scrollHeight - el.scrollTop - el.clientHeight <= threshold;
    setShowScrollToBottom(!atBottom);
  };

  useEffect(() => {
    handleChatScroll();
  }, [messages.length, isSubmitted]);

  const handleStreamResponse = (chunk: string) => {
    try {
      const cleaned = chunk.replace(/--KEEP-ALIVE--/g, "");
      const segments = cleaned.split("--CLEAR--");

      const progressEmojis = ["🧠", "📑", "🔎", "📚", "⚙️", "🔁", "🔍", "📝"];
      const progressKeywords = [
        "analyse",
        "analy",
        "retrieve",
        "collect",
        "search query",
        "iteration",
        "extract",
        "generate",
        "writing",
        "agent started",
        "answer",
      ];

      const isProgressSegment = (p: string): boolean => {
        if (!p) return false;
        const trimmed = p.trim();
        if (!trimmed) return false;
        if (progressEmojis.some((e) => trimmed.startsWith(e))) return true;
        const low = trimmed.toLowerCase();
        return progressKeywords.some((k) => low.includes(k));
      };

      segments.forEach((seg) => {
        const part = (seg || "").trim();
        if (!part) return;

        if (/--RESPONSE--/i.test(part)) {
          const after = part.replace(/--RESPONSE--/i, "").trim();

          streamingBuffer.current += after;

          setMessages((prevMessages) => {
            const updated = [...prevMessages];
            const lastAssistantIndex = [...updated]
              .reverse()
              .findIndex((m) => m.role === "assistant");

            const finalText = streamingBuffer.current || "";

            if (lastAssistantIndex !== -1) {
              const realIndex = updated.length - 1 - lastAssistantIndex;
              updated[realIndex] = { role: "assistant", text: finalText };
            } else {
              updated.push({ role: "assistant", text: finalText });
            }

            return updated;
          });

          streamingBuffer.current = "";
          isWaitingPhase.current = false;
          setWaitingMessage("");
          setWaitingSteps([]);
          return;
        }

        if (isProgressSegment(part)) {
          isWaitingPhase.current = true;
          const normalizedPart = part.replace(/\s+/g, " ").trim();
          if (!normalizedPart) return;
          setWaitingMessage(normalizedPart);
          setWaitingSteps((prev) =>
            prev[prev.length - 1] === normalizedPart
              ? prev
              : [...prev, normalizedPart],
          );
          return;
        }

        streamingBuffer.current += part;
      });
    } catch (err) {
      console.error("stream handling error:", err);
    }
  };

  const renderChunksFromCache = () => {
    isRendering.current = true;
    const CHUNKS_PER_RENDER = 2;

    function renderStep() {
      if (chunkCache.current.length === 0) {
        isRendering.current = false;
        return;
      }

      const toRender = chunkCache.current.splice(0, CHUNKS_PER_RENDER);
      setMessages((prevMessages): IMessage[] => {
        const lastMessageIndex = prevMessages.length - 1;
        const lastMessage = prevMessages[lastMessageIndex];
        if (lastMessage && lastMessage.role === "assistant") {
          const updated = [...prevMessages];
          updated[lastMessageIndex] = {
            ...lastMessage,
            text: lastMessage.text + toRender.join(""),
          };
          return updated;
        }
        return [
          ...prevMessages,
          {
            role: "assistant",
            text: toRender.join(""),
          },
        ];
      });

      setTimeout(renderStep, 350);
    }

    renderStep();
  };

  const getMessagesAfterLastInfo = (allMessages: IMessage[]): IMessage[] => {
    const lastInfoIndex = allMessages
      .map((msg) => msg.role)
      .lastIndexOf("info");
    return lastInfoIndex >= 0
      ? allMessages.slice(lastInfoIndex + 1)
      : allMessages;
  };

  const handleCopyClick = async (index: number) => {
    await handleCopyToClipboard({ current: markdownRefs.current[index] });
    setCopiedToBuffer((prev) => ({ ...prev, [index]: true }));
    if (copyTimeouts.current[index]) {
      window.clearTimeout(copyTimeouts.current[index]);
    }
    copyTimeouts.current[index] = window.setTimeout(() => {
      setCopiedToBuffer((prev) => ({ ...prev, [index]: false }));
    }, 3000);
  };

  const openEditPanel = (index: number) => {
    const currentText = messages[index]?.text ?? "";
    setEditIndex(index);
    setEditDraft(currentText);
    setIsEditOpen(true);
  };

  const closeEditPanel = () => {
    setIsEditOpen(false);
    setEditIndex(null);
    setEditDraft("");
  };

  const saveEdit = () => {
    if (editIndex === null) return;
    const nextText = editDraft.trim();
    if (!nextText) return;

    setMessages((prev) => {
      const updated = [...prev];
      if (updated[editIndex] && updated[editIndex].role === "assistant") {
        updated[editIndex] = { ...updated[editIndex], text: nextText };
      }
      return updated;
    });

    closeEditPanel();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    autoResize(e.target);
    setValueInput(val);
  };

  const handleSubmit = (val: string, isRetry = false) => {
    if (val.trim() && !isLimitReached) {
      setValueConversationInput("");
      setIsSubmitted(true);
      setIsModalOpen(false);
      setValueInput(val);

      if (!isRetry) {
        setMessages((prevMessages) => [
          ...prevMessages,
          { role: "user", text: val },
          { role: "assistant", text: "" },
        ]);
      } else {
        setMessages((prevMessages) => {
          const updated = [...prevMessages];
          const last = updated[updated.length - 1];
          if (!(last && last.role === "assistant" && last.text === "")) {
            updated.push({ role: "assistant", text: "" });
          }
          return updated;
        });
      }

      setWaitingMessage("");
      isWaitingPhase.current = true;
      setWaitingSteps([]);

      const lastMessage = messages[messages.length - 1];
      const isDuplicate =
        lastMessage?.role === "user" && lastMessage?.text === val;
      setConversationHistory(
        isDuplicate
          ? messages
          : [...messages, { role: "user", text: val } as IMessage],
      );
      fetchChatResponse();
      setScrollToLastUser(true);

      if (convInputRef.current) {
        convInputRef.current.style.height = "auto";
        convInputRef.current.rows = 1;
      }
    }
  };

  useEffect(() => {
    if (
      scrollToLastUser &&
      lastUserMsgRef.current &&
      chatContainerRef.current
    ) {
      setTimeout(() => {
        chatContainerRef.current!.scrollTop =
          lastUserMsgRef.current!.offsetTop -
          chatContainerRef.current!.offsetTop;
        setScrollToLastUser(false);
      }, 0);
    }
  }, [scrollToLastUser, messages]);

  const handleConvInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    e.target.style.height = "auto";
    e.target.style.height = `${e.target.scrollHeight}px`;
    setValueConversationInput(val);
  };

  const handleParsePrompt = (val: string) => {
    if (isSubmitted) {
      setValueConversationInput(val);
      setValueInput("");
      requestAnimationFrame(() => autoResize(convInputRef.current));
    } else {
      setValueInput(val);
      setValueConversationInput("");
      requestAnimationFrame(() => autoResize(landingInputRef.current));
    }

    setIsPanelCollapsed(true);
    setIsModalOpen(false);
  };

  const clearHistory = () => {
    setMessages([]);
    setConversationHistory([]);
  };

  const downloadConv = () => {
    exportChat();
  };

  useEffect(() => {
    if (!isPanelCollapsed) {
      setIsModalOpen(false);
    }
  }, [isPanelCollapsed]);

  useEffect(() => {
    const backup = localStorage.getItem("details_chatAI_backup");
    if (backup) {
      try {
        const parsed = JSON.parse(backup);
        if (parsed?.messages?.length > 0) {
          setMessages(parsed.messages);
          setIsSubmitted(true);

          if (parsed.outputAdj) setOutputAdj(parsed.outputAdj);

          const lastUserMsg = [...parsed.messages]
            .reverse()
            .find((m) => m.role === "user")?.text;
          if (lastUserMsg) {
            setTimeout(() => {
              fetchChatResponse(parsed.messages);
            }, 3000);
          }
        }
      } catch (e) {
        console.error("Failed to restore chat after relogin:", e);
      } finally {
        localStorage.removeItem("details_chatAI_backup");
      }
    }
  }, []);

  const handleKeyDown =
    (valueGetter: () => string) =>
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();

        const value = valueGetter();
        if (value.trim()) {
          handleSubmit(value);
        }
      }
    };

  return (
    <div className="text-secondary-color flex flex-col h-full min-h-0 relative">
      <div className="w-full transition-all duration-300 flex flex-col border-gray-300 flex-grow min-h-0">
        {isDataFound &&
          (!isSubmitted ? (
            <div className="flex flex-col flex-grow min-h-0 bg-white dark:bg-slate-900 text-gray-600 dark:text-white rounded-xl">
              <div
                className={`w-full flex flex-col items-center ${
                  isPanelCollapsed ? "flex-1 justify-center" : "pt-16 pb-4"
                }`}
              >
                <h1 className="text-xl font-semibold mb-10 flex text-center">
                  {t("chatAI.howMayIHelp")}
                </h1>

                <div className="w-3/4 mb-3">
                  <ChatLimitBanner />
                </div>

                <div className="w-3/4 mb-4 flex items-center gap-2">
                  <div className="flex-1 relative">
                    <textarea
                      ref={landingInputRef}
                      disabled={isLimitReached}
                      placeholder={t("chatAI.askLegalQuestion")}
                      className="w-full rounded-xl border border-slate-300 dark:border-slate-600 bg-white/90 dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 resize-none overflow-y-hidden max-h-[288px] min-h-[40px] text-sm px-4 py-2.5 pr-12 focus:outline-none focus:ring-2 focus:ring-del-green"
                      value={valueInput}
                      onChange={handleInputChange}
                      onKeyDown={handleKeyDown(() => valueInput)}
                      rows={1}
                    />
                    {!!valueInput.trim() && !isLimitReached && (
                      <button
                        className="absolute right-2 bottom-2 h-9 w-9 flex items-center justify-center rounded-full text-del-green hover:text-del-light-theme-green"
                        onClick={() => handleSubmit(valueInput)}
                      >
                        <FiSend />
                      </button>
                    )}
                  </div>

                  <div className="self-center -translate-y-0.5 flex items-center gap-2">
                    <button
                      className={`tooltip-open-outputAdj h-10 w-10 flex items-center justify-center rounded-lg border ${
                        isModalOpen
                          ? "border-del-green bg-del-green/10 text-del-light-theme-green dark:bg-del-green/20"
                          : "border-slate-300 dark:border-slate-600 bg-white/90 dark:bg-slate-800 text-slate-600 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
                      }`}
                      onClick={() => setIsModalOpen(!isModalOpen)}
                    >
                      <FiSliders />
                    </button>

                    <button
                      className={`tooltip-landing-promptlib h-10 w-10 flex items-center justify-center rounded-lg border ${
                        !isPanelCollapsed
                          ? "border-del-green bg-del-green/10 text-del-light-theme-green dark:bg-del-green/20"
                          : "border-slate-300 dark:border-slate-600 bg-white/90 dark:bg-slate-800 text-slate-600 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
                      }`}
                      onClick={() => setIsPanelCollapsed((prev) => !prev)}
                    >
                      <FiBookOpen />
                    </button>
                  </div>

                  {isModalOpen && (
                    <ModalOutputAdjustments
                      onClose={() => setIsModalOpen(false)}
                      isSubmit={false}
                      onOutputAdjChange={(outputs: IOutputAdjustment) =>
                        setOutputAdj(outputs)
                      }
                      outputAdj={outputAdj}
                    />
                  )}
                </div>
              </div>

              <Tooltip
                anchorSelect=".tooltip-landing-promptlib"
                place="top"
                className="text-xs font-light z-50"
                content={
                  isPanelCollapsed
                    ? t("chatAI.openPromptLibraryTooltip")
                    : t("chatAI.closePromptLibraryTooltip")
                }
              />

              {!isPanelCollapsed && (
                <div className="w-full flex-1 min-h-0 flex justify-center pb-4">
                  <div className="w-3/4 min-h-0 overflow-hidden">
                    <PromptBlock
                      isPanelCollapsed={isPanelCollapsed}
                      setIsPanelCollapsed={setIsPanelCollapsed}
                      handleSubmit={handleParsePrompt}
                      isSubmitted={false}
                    />
                  </div>
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="flex-1 min-h-0 relative flex flex-col">
                <div
                  className="flex-1 min-h-0 overflow-y-auto space-y-3 pt-4 pb-1 px-4"
                  ref={chatContainerRef}
                  onScroll={handleChatScroll}
                >
                  {messages.map((message, index) => (
                    <React.Fragment key={`message-fragment-${index}`}>
                      {message.role === "assistant" &&
                      message.text === "" &&
                      isPending ? (
                        <div className="w-full flex justify-start">
                          <div className="max-w-[520px] w-full rounded-xl bg-white/80 dark:bg-slate-800/70 px-4 py-3 text-sm text-slate-600 dark:text-slate-300">
                            {waitingSteps.length === 0 ? (
                              <div className="flex items-center gap-2">
                                <FaSpinner className="animate-spin text-xs" />
                              </div>
                            ) : (
                              <div className="relative flex flex-col gap-0">
                                <div className="absolute left-2 top-2 bottom-2 w-px bg-slate-300 dark:bg-slate-600 z-0" />
                                {waitingSteps.map((step, idx) => {
                                  const activeIdx = waitingSteps.length - 1;
                                  const isActive = idx === activeIdx;
                                  const isDone = idx < activeIdx;

                                  return (
                                    <div
                                      key={`waiting-step-${idx}`}
                                      className="relative"
                                    >
                                      <div
                                        className={[
                                          "flex items-start gap-2 py-1.5",
                                          isActive
                                            ? "text-slate-700 dark:text-slate-100 font-medium"
                                            : isDone
                                              ? "text-slate-400 dark:text-slate-500"
                                              : "text-slate-500 dark:text-slate-400",
                                        ].join(" ")}
                                      >
                                        <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 relative z-10 bg-white/80 dark:bg-slate-800/70">
                                          {isDone ? (
                                            <span className="w-2 h-2 rounded-full bg-slate-400 dark:bg-slate-500" />
                                          ) : isActive ? (
                                            <FaSpinner className="animate-spin text-xs" />
                                          ) : (
                                            ""
                                          )}
                                        </span>
                                        <span className="flex items-start gap-2 min-w-0">
                                          {getStepIcon(step)}
                                          <span>{stripStepEmoji(step)}</span>
                                        </span>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div
                          className={`w-full flex ${
                            message.role === "user"
                              ? "justify-end"
                              : "justify-start"
                          }`}
                        >
                          <div className="inline-flex flex-col items-start">
                            <div
                              ref={(el) => {
                                markdownRefs.current[index] = el;

                                if (index === lastUserMsgIdx) {
                                  lastUserMsgRef.current = el;
                                }
                              }}
                              className={`relative inline-block text-left break-words text-gray-800 dark:text-white ${
                                message.role === "user"
                                  ? "whitespace-pre-wrap max-w-max p-3 bg-del-green/20 dark:bg-del-green/25 rounded-xl rounded-br-none border border-slate-200 dark:border-slate-700"
                                  : message.role === "assistant"
                                    ? "max-w-max p-0"
                                    : "max-w-max m-auto text-sm"
                              }`}
                            >
                              {message.role === "assistant" &&
                              isEditOpen &&
                              editIndex === index ? (
                                <div className="w-full max-w-[900px]">
                                  <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-2">
                                    <MarkdownEditor
                                      data={editDraft}
                                      onChange={setEditDraft}
                                      className="mdx-chat-editor min-h-[280px] px-3 py-2"
                                    />
                                  </div>
                                  <div className="mt-3 flex items-center gap-2">
                                    <button
                                      type="button"
                                      onClick={saveEdit}
                                      className="px-2 py-1 text-xs font-medium text-white bg-del-green rounded-md hover:bg-del-light-theme-green"
                                    >
                                      {t("chatAI.saveEdit", {
                                        defaultValue: "Save",
                                      })}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={closeEditPanel}
                                      className="px-2 py-1 text-xs font-medium text-slate-600 dark:text-slate-200 border border-slate-200 dark:border-slate-600 rounded-md hover:bg-slate-50 dark:hover:bg-slate-700"
                                    >
                                      {t("chatAI.cancelEdit", {
                                        defaultValue: "Cancel",
                                      })}
                                    </button>
                                  </div>
                                </div>
                              ) : message.role !== "info" ? (
                                <Markdown
                                  components={{
                                    a: ({ node, ...props }) => (
                                      <a
                                        target="_blank"
                                        className="text-blue-500 dark:text-blue-300"
                                        {...props}
                                      />
                                    ),
                                  }}
                                  rehypePlugins={[rehypeRaw]}
                                  remarkPlugins={[remarkGfm, remarkBreaks]}
                                  className={
                                    message.role === "user"
                                      ? styles.markdownBlock + " flex-grow"
                                      : styles.markdownChatAIBlock + " flex-grow"
                                  }
                                >
                                  {message.text}
                                </Markdown>
                              ) : (
                                t(message.text)
                              )}
                            </div>

                            {message.role === "assistant" &&
                              message.text.trim() !== "" &&
                              !isWaitingPhase.current &&
                              (!isPending || index !== lastAssistantMsgIdx) &&
                              !(isEditOpen && editIndex === index) && (
                                <div className="mt-1 self-end flex items-center gap-2">
                                  <button
                                    type="button"
                                    onClick={() => openEditPanel(index)}
                                    className={`tooltip-edit-${index} text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300 transition-colors focus:outline-none`}
                                  >
                                    <FiEdit2 size={16} />
                                  </button>
                                  <Tooltip
                                    anchorSelect={`.tooltip-edit-${index}`}
                                    place="top"
                                    positionStrategy="fixed"
                                    closeOnScroll
                                    className="text-xs font-light z-50"
                                  >
                                    {t("chatAI.edit", { defaultValue: "Edit" })}
                                  </Tooltip>

                                  {(() => {
                                    const isPolandReg =
                                      regulator &&
                                      typeof regulator === "string" &&
                                      (regulator
                                        .toLowerCase()
                                        .includes("poland") ||
                                        regulator
                                          .toLowerCase()
                                          .startsWith("pl"));

                                    if (!isPolandReg) return null;

                                    return (
                                      <>
                                        <button
                                          type="button"
                                          onClick={async () => {
                                            try {
                                              setIsGeneratingMemoIndex(index);

                                              const created = await createMemoJob(
                                                message.text,
                                              );
                                              toast.info("Memo generation started. Please wait...");



                                              const finalJob = await waitForMemoJob(
                                                created.job_id,
                                                (job) => {
                                                  if (job.status === "pending") {
                                                    // queued
                                                  } else if (job.status === "running") {
                                                  }
                                                },
                                                2000,
                                                20 * 60 * 1000,
                                              );

                                              if (finalJob.status === "done") {
                                                try {
                                                  await downloadMemoJob(created.job_id);
                                                  toast.success("Memos are ready");
                                                } catch (e) {
                                                  throw e;
                                                }
                                              }
                                              } catch (err: any) {
                                              console.error("generateMemos error:", err);
                                              let message = "Failed to generate memos";
                                              const resp = err?.response?.data;
                                              if (resp) {
                                                if (typeof resp === "string") message = resp;
                                                else if (resp.error) message = resp.error;
                                                else if (resp.detail) message = resp.detail;
                                                else if (resp.error_message) message = resp.error_message;
                                              } else if (err?.message) {
                                                message = err.message;
                                              }
                                              toast.error(message);
                                            } finally {
                                              setIsGeneratingMemoIndex(null);
                                            }
                                          }}
                                          className={`tooltip-gen-memo-${index} text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300 transition-colors focus:outline-none`}
                                        >
                                          {isGeneratingMemoIndex === index ? (
                                            <FaSpinner className="animate-spin" />
                                          ) : (
                                            <FiFileText size={16} />
                                          )}
                                        </button>
                                        <Tooltip
                                          anchorSelect={`.tooltip-gen-memo-${index}`}
                                          place="top"
                                          positionStrategy="fixed"
                                          closeOnScroll
                                          className="text-xs font-light z-50"
                                        >
                                          {t("chatAI.generateMemo", {
                                            defaultValue: "Generate memo",
                                          })}
                                        </Tooltip>
                                      </>
                                    );
                                  })()}

                                  <button
                                    type="button"
                                    onClick={() => handleCopyClick(index)}
                                    className={`tooltip-context-${index} text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300 transition-colors focus:outline-none`}
                                  >
                                    {copiedToBuffer[index] ? (
                                      <FiCheck size={16} />
                                    ) : (
                                      <IoCopy size={16} />
                                    )}
                                  </button>
                                  <Tooltip
                                    anchorSelect={`.tooltip-context-${index}`}
                                    place="top"
                                    positionStrategy="fixed"
                                    closeOnScroll
                                    className="text-xs font-light z-50"
                                  >
                                    {copiedToBuffer[index]
                                      ? t("chatAI.copied")
                                      : t("chatAI.copyText")}
                                  </Tooltip>
                                </div>
                              )}
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  ))}

                  {isError && messages.length > 0 && !isLimitReached && (
                    <div className="flex flex-col items-center justify-center text-center text-gray-500 dark:text-gray-400">
                      <TbRotateClockwise
                        className="text-4xl cursor-pointer hover:text-del-green transition-colors"
                        onClick={() => {
                          const lastUserMsg = [...messages]
                            .reverse()
                            .find((m) => m.role === "user")?.text;
                          if (lastUserMsg) {
                            handleSubmit(lastUserMsg);
                          }
                        }}
                      />
                      <p className="mt-2 text-sm">{t("retryMessage")}</p>
                    </div>
                  )}
                </div>

                {showScrollToBottom && (
                  <button
                    className="tooltip-details-scroll-bottom absolute bottom-3 right-3 h-9 w-9 flex items-center justify-center rounded-full border border-slate-200 dark:border-slate-700 bg-white/95 dark:bg-slate-800 text-slate-700 dark:text-slate-200 shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700"
                    onClick={scrollChatToBottom}
                  >
                    <FiChevronDown />
                  </button>
                )}
              </div>

              {isSubmitted && !isPanelCollapsed && (
                <div className="px-3 pb-2 flex-shrink-0">
                  <div className="h-[320px] min-h-0">
                    <PromptBlock
                      isPanelCollapsed={isPanelCollapsed}
                      setIsPanelCollapsed={setIsPanelCollapsed}
                      handleSubmit={handleParsePrompt}
                      isSubmitted={isSubmitted}
                    />
                  </div>
                </div>
              )}

              <div className="px-3 py-2 flex-shrink-0">
                <div className="mb-2">
                  <ChatLimitBanner />
                </div>
                <div className="relative flex items-end gap-2 pr-[180px]">
                  <div className="flex items-center gap-2 flex-grow">
                    <div className="flex-1 relative">
                      <textarea
                        ref={convInputRef}
                        disabled={isLimitReached}
                        placeholder={t("chatAI.askLegalQuestion")}
                        className="w-full rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 resize-none overflow-y-hidden max-h-[288px] min-h-[40px] text-sm leading-5 px-4 py-3 pr-14 focus:outline-none focus:ring-2 focus:ring-del-green"
                        value={valueConversationInput}
                        onChange={handleConvInputChange}
                        onKeyDown={handleKeyDown(
                          () => valueConversationInput,
                        )}
                        rows={1}
                      />
                      {!!valueConversationInput.trim() && !isLimitReached && (
                        <button
                          className="absolute right-2 bottom-2 h-9 w-9 flex items-center justify-center rounded-full text-del-green hover:text-del-light-theme-green"
                          onClick={() => handleSubmit(valueConversationInput)}
                          disabled={isPending}
                        >
                          <FiSend />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="absolute right-0 bottom-2 flex items-center gap-2">
                    <button
                      className={`tooltip-open-outputAdj h-9 w-9 flex items-center justify-center rounded-lg border ${
                        isModalOpen
                          ? "border-del-green bg-del-green/10 text-del-light-theme-green dark:bg-del-green/20"
                          : "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
                      }`}
                      onClick={() => setIsModalOpen(!isModalOpen)}
                    >
                      <FiSliders />
                    </button>

                    <button
                      className={`tooltip-details-promptlib h-9 w-9 flex items-center justify-center rounded-lg border ${
                        !isPanelCollapsed
                          ? "border-del-green bg-del-green/10 text-del-light-theme-green dark:bg-del-green/20"
                          : "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
                      }`}
                      onClick={() => setIsPanelCollapsed((prev) => !prev)}
                    >
                      <FiBookOpen />
                    </button>

                    <button
                      className={`tooltip-details-clear-chatai-conv h-9 w-9 flex items-center justify-center rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-red-600 dark:text-red-400 ${
                          isBusy
                            ? "cursor-not-allowed opacity-50"
                            : "hover:bg-red-50 dark:hover:bg-red-500/10"
                        }`}
                        onClick={() => {
                          if (!isBusy) clearHistory();
                        }}
                        disabled={isBusy}
                    >
                      <FiTrash />
                    </button>

                    <button
                      className={`${
                        messages.length === 0 || isPending
                          ? "cursor-not-allowed opacity-50"
                          : "tooltip-details-export-chatai-conv hover:bg-del-green dark:hover:bg-del-green hover:text-white"
                      } h-9 w-9 flex items-center justify-center rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200`}
                      onClick={() => {
                        if (!isPending) downloadConv();
                      }}
                      disabled={messages.length === 0 || isPending}
                    >
                      <FiDownload />
                    </button>
                  </div>
                </div>

                <Tooltip
                  anchorSelect=".tooltip-details-export-chatai-conv"
                  place="top"
                  className="text-xs font-light z-50"
                  content={t("chatAI.exportChatConvTooltip")}
                />
                <Tooltip
                  anchorSelect=".tooltip-details-promptlib"
                  place="top"
                  className="text-xs font-light z-50"
                  content={
                    isPanelCollapsed
                      ? t("chatAI.openPromptLibraryTooltip")
                      : t("chatAI.closePromptLibraryTooltip")
                  }
                />
                <Tooltip
                  anchorSelect=".tooltip-details-clear-chatai-conv"
                  place="top"
                  className="text-xs font-light z-50"
                  content={t("chatAI.clearChatConvTooltip")}
                />
                <Tooltip
                  anchorSelect=".tooltip-details-scroll-bottom"
                  place="top"
                  className="text-xs font-light z-50"
                  content={t("chatAI.scrollToBottom", {
                    defaultValue: "Scroll to bottom",
                  })}
                />
                <Tooltip
                  anchorSelect=".tooltip-open-outputAdj"
                  place="top"
                  className="text-xs font-light z-50"
                  content={t("chatAI.openOutputAdjTooltip")}
                />
              </div>

              {isModalOpen && (
                <ModalOutputAdjustments
                  onClose={() => setIsModalOpen(false)}
                  isSubmit={true}
                  onOutputAdjChange={(outputs: IOutputAdjustment) =>
                    setOutputAdj(outputs)
                  }
                  outputAdj={outputAdj}
                />
              )}
            </>
          ))}
      </div>
    </div>
  );
};

export { ChatAI };